import React from 'react';
import { Button } from '@/components/ui/button';
import { Star, ExternalLink } from 'lucide-react';
import { Display } from '@/types/sequence';
import AvatarUpload from './AvatarUpload';

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  bio?: string;
  avatar_url?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

interface UserProfileHeaderProps {
  user: UserProfile;
  userDisplay: Display | null;
  onEditProfile: () => void;
  isPublicView?: boolean;
}

const UserProfileHeader: React.FC<UserProfileHeaderProps> = ({
  user,
  userDisplay,
  onEditProfile,
  isPublicView = false
}) => {
  const handleAvatarUpdated = (url: string) => {
    // The avatar update is handled in the AvatarUpload component
    // We don't need to do anything here as the user metadata is updated automatically
  };

  const handleViewPublicProfile = () => {
    // Generate public profile URL
    const username = user?.username || 
                    user?.display_name?.toLowerCase().replace(/\s+/g, '') || 
                    'user';
    
    const publicUrl = `/profile/${username}`;
    window.open(publicUrl, '_blank');
  };

  return (
    <div className="bg-background rounded-lg shadow-xs border p-6">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="shrink-0">
          {!isPublicView ? (
            <AvatarUpload 
              currentAvatarUrl={user?.avatar_url}
              userId={user?.user_id}
              onAvatarUpdated={handleAvatarUpdated}
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-linear-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              {user?.avatar_url ? (
                <img 
                  src={user.avatar_url} 
                  alt={user.display_name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-xl font-bold">
                  {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="grow">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {user?.display_name || 'User'}
          </h1>
          
          {user?.username && (
            <p className="text-base text-muted-foreground mb-2">@{user.username}</p>
          )}
          
          {user?.bio && (
            <p className="text-muted-foreground mb-3">{user.bio}</p>
          )}
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {userDisplay && (
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                <span>{userDisplay.location}</span>
              </div>
            )}
          </div>
        </div>
        
        {!isPublicView && (
          <div className="flex gap-3 mt-2 md:mt-0">
            <Button variant="outline" onClick={onEditProfile}>Edit Profile</Button>
            <Button 
              variant="outline" 
              onClick={handleViewPublicProfile}
              className="flex items-center gap-2"
            >
              <ExternalLink size={16} />
              View Public Profile
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileHeader;
